import React, { useState, useEffect, useRef, useCallback } from 'react';
import { renderIcon } from '../../../utils/icons';
import { getPhaseEmoji } from '../../../utils/site-constants';
import { api } from '../../../utils/api';
import WorkstreamCard from './WorkstreamCard';

export default function PhaseSection({ phase, gate, workstreams, users, canAdmin, isDark, theme, mobile, updateItem, deleteItem, onGateAction, siteId, prevPhaseComplete }) {
  const [collapsed, setCollapsed] = useState(() => {
    const s = gate.status || 'active';
    return s === 'complete' || s === 'locked';
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [localWs, setLocalWs] = useState(workstreams);

  /* Drag-to-scroll for horizontal card container */
  const scrollRef = useRef(null);
  const scrollState = useRef({ down: false, startX: 0, scrollLeft: 0 });
  const onScrollDown = useCallback((e) => {
    if (e.target.closest('button, a, input, select, textarea, [draggable="true"]')) return;
    const el = scrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    scrollState.current = { down: true, startX: e.pageX || e.touches?.[0]?.pageX, scrollLeft: el.scrollLeft };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  }, []);
  const onScrollMove = useCallback((e) => {
    if (!scrollState.current.down) return;
    const el = scrollRef.current;
    const x = e.pageX || e.touches?.[0]?.pageX;
    el.scrollLeft = scrollState.current.scrollLeft - (x - scrollState.current.startX);
  }, []);
  const onScrollUp = useCallback(() => {
    scrollState.current.down = false;
    if (scrollRef.current) { scrollRef.current.style.cursor = ''; scrollRef.current.style.userSelect = ''; }
  }, []);

  useEffect(() => { setLocalWs(workstreams); }, [workstreams]);

  const gateStatus = gate.status || 'active';

  const totalWs = workstreams.length;
  const completeWs = workstreams.filter(ws => {
    const items = ws.items || [];
    const required = items.filter(i => i.is_required !== false);
    return required.length > 0 && required.every(i => i.status === 'Complete');
  }).length;

  const allRequired = workstreams.flatMap(ws => (ws.items || []).filter(i => i.is_required !== false));
  const allRequiredDone = allRequired.filter(i => i.status === 'Complete').length;
  const phasePct = allRequired.length > 0 ? Math.round((allRequiredDone / allRequired.length) * 100) : 100;
  const maxThreshold = Math.max(...workstreams.map(ws => ws.gate_threshold || 100), 0);
  const thresholdMet = phasePct >= maxThreshold;

  const badgeColor = gateStatus === 'complete' ? '#4ADE80' : gateStatus === 'active' ? '#4ADE80' : '#6b7280';
  const badgeText = gateStatus === 'complete' ? 'COMPLETE' : gateStatus === 'active' ? 'ACTIVE' : 'LOCKED';

  let actionBtn = null;
  if (canAdmin) {
    if (gateStatus === 'active' && thresholdMet) {
      actionBtn = (
        <button
          onClick={() => onGateAction && onGateAction(siteId, phase, 'complete')}
          style={{
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: '#4ADE80', color: '#0A0A0A', fontSize: 12,
            fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          ✓ Mark Phase Complete
        </button>
      );
    } else if (gateStatus === 'locked' && prevPhaseComplete) {
      actionBtn = (
        <button
          onClick={() => onGateAction && onGateAction(siteId, phase, 'active')}
          style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid ' + theme.border,
            background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.08)',
            color: '#4ADE80', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {renderIcon('unlock', 14)} Unlock Phase
        </button>
      );
    }
  }

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: collapsed ? 8 : 16, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none',
      }} onClick={(e) => { if (e.target.tagName !== 'BUTTON') setCollapsed(!collapsed); }}>
        <span style={{ fontSize: 16, color: theme.textSecondary, transition: 'transform 0.2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
        <span style={{ fontSize: 22, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text }}>
          {getPhaseEmoji(phase, 22)} {phase}
        </span>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
          background: badgeColor + '22', color: badgeColor, letterSpacing: 0.5,
          textTransform: 'uppercase',
        }}>
          {badgeText}
        </span>
        <span style={{ fontSize: 13, color: theme.textSecondary }}>
          {completeWs}/{totalWs} workstreams complete
        </span>
        <div style={{ flex: 1 }} />
        {actionBtn}
      </div>

      {!collapsed && (
        <div
          ref={scrollRef}
          onMouseDown={!mobile ? onScrollDown : undefined}
          onMouseMove={!mobile ? onScrollMove : undefined}
          onMouseUp={!mobile ? onScrollUp : undefined}
          onMouseLeave={!mobile ? onScrollUp : undefined}
          onTouchStart={!mobile ? onScrollDown : undefined}
          onTouchMove={!mobile ? onScrollMove : undefined}
          onTouchEnd={!mobile ? onScrollUp : undefined}
          className="phase-scroll-row"
          style={{
            display: 'flex',
            flexDirection: mobile ? 'column' : 'row',
            gap: 16,
            overflowX: mobile ? 'visible' : 'auto',
            paddingBottom: mobile ? 0 : 8,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            cursor: !mobile ? 'grab' : undefined,
          }}>
          {localWs.map((ws, idx) => (
            <div
              key={ws.id}
              draggable={canAdmin}
              onDragStart={(e) => {
                setDragIdx(idx);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', idx.toString());
                setTimeout(() => e.target.style.opacity = '0.4', 0);
              }}
              onDragEnd={(e) => {
                e.target.style.opacity = '1';
                setDragIdx(null);
                setDragOverIdx(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setDragOverIdx(idx);
              }}
              onDragLeave={() => {
                setDragOverIdx(null);
              }}
              onDrop={async (e) => {
                e.preventDefault();
                const fromIdx = dragIdx;
                if (fromIdx === null || fromIdx === idx) return;
                const newWs = [...localWs];
                const [moved] = newWs.splice(fromIdx, 1);
                newWs.splice(idx, 0, moved);
                setLocalWs(newWs);
                setDragIdx(null);
                setDragOverIdx(null);
                const reordered = newWs.map((w, i) => ({ id: w.id, sort_order: i }));
                try {
                  await api.post('/api/workstreams/reorder', { workstreams: reordered });
                } catch (err) {
                  console.error('Reorder failed:', err);
                  setLocalWs(workstreams);
                }
              }}
              style={{
                cursor: canAdmin ? 'grab' : 'default',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                transform: dragOverIdx === idx && dragIdx !== idx ? 'scale(1.02)' : 'scale(1)',
                borderLeft: dragOverIdx === idx && dragIdx !== null && dragIdx !== idx ? '3px solid #4ADE80' : '3px solid transparent',
                borderRadius: 12,
              }}
            >
              <WorkstreamCard
                ws={ws}
                gate={gate}
                users={users}
                canAdmin={canAdmin}
                isDark={isDark}
                theme={theme}
                mobile={mobile}
                updateItem={updateItem}
                deleteItem={deleteItem}
                siteId={siteId}
              />
            </div>
          ))}
        </div>
      )}

      {collapsed && (
        <div style={{
          padding: '8px 12px', background: 'rgba(255,255,255,0.03)',
          borderRadius: 8, border: '1px solid ' + theme.border, marginBottom: 8,
          fontSize: 12, color: theme.textSecondary, display: 'flex', gap: 16, flexWrap: 'wrap'
        }}>
          {localWs.map(ws => {
            const items = ws.items || [];
            const done = items.filter(i => i.status === 'Complete').length;
            return (
              <span key={ws.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontWeight: 600, color: theme.text }}>{ws.name}</span>
                <span style={{ color: done === items.length && items.length > 0 ? '#4ADE80' : theme.textSecondary }}>{done}/{items.length}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
